import { DURATION, EASE, SHAKE_TRANSITION, useReducedMotion } from '@/lib/motion-presets';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';

const PIN_LENGTH = 4;

type AuthPinDotsProps = {
	pin: string;
	error?: string | null;
	shakeKey?: number;
	isSubmitting?: boolean;
};

export default function AuthPinDots({ pin, error, shakeKey = 0, isSubmitting }: AuthPinDotsProps) {
	const reducedMotion = useReducedMotion();
	const hasError = Boolean(error);

	return (
		<motion.div
			key={shakeKey}
			className='flex items-center justify-center gap-3'
			animate={
				hasError && !reducedMotion
					? { x: [-8, 8, -6, 6, 0] }
					: { x: 0 }
			}
			transition={SHAKE_TRANSITION}
		>
			{Array.from({ length: PIN_LENGTH }, (_, index) => {
				const filled = index < pin.length;
				const isActive = index === pin.length && pin.length < PIN_LENGTH;

				return (
					<motion.div
						key={index}
						className={cn(
							'relative flex h-14 w-14 items-center justify-center rounded-xl border-2 transition-colors',
							hasError
								? 'border-destructive/70 bg-destructive/5'
								: filled
									? 'border-[var(--auth-red)]/60 bg-[var(--auth-red-muted)]'
									: 'border-white/15 bg-white/5',
							isSubmitting && 'opacity-70',
						)}
						animate={
							filled && !reducedMotion
								? { scale: [1, 1.12, 1] }
								: { scale: 1 }
						}
						transition={{ duration: DURATION, ease: EASE }}
					>
						{filled ? (
							<motion.div
								className='h-3 w-3 rounded-full bg-[var(--auth-white)]'
								initial={reducedMotion ? false : { scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ type: 'spring', stiffness: 500, damping: 28 }}
							/>
						) : isActive ? (
							<div className='h-4 w-px animate-caret-blink bg-[var(--auth-white)] duration-1000' />
						) : null}
					</motion.div>
				);
			})}
		</motion.div>
	);
}
